// CourseRegistrationBarGraph.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";

export default function CourseRegistrationBarGraph({ analytics, onFilterChange }) {

  const [view, setView] = useState("total");
  const [monthWeek, setMonthWeek] = useState("all");

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  /* ================= FILTER ================= */

  useEffect(() => {
    if (!onFilterChange) return;

    onFilterChange({
      view,
      year,
      month,
      week: monthWeek === "all" ? 0 : monthWeek - 1
    });

  }, [view, year, month, monthWeek]);

  /* ================= DATA PROCESS ================= */

  const graphData = useMemo(() => {

    if (!analytics) return null;

    const data = analytics;

    if (view === "total") {
      return {
        labels: ["Total"],
        data: [{
          success: data.total.success,
          pending: data.total.pending,
          refunded: data.total.refunded,
          paused: data.total.paused
        }]
      };
    }

    if (view === "year") {
      return {
        labels: data.months.map(m => m.month),
        data: data.months.map(m => ({
          success: m.success,
          pending: m.pending,
          refunded: m.refunded,
          paused: m.paused
        }))
      };
    }

    if (view === "month") {

      let source = [];

      if (monthWeek === "all") {
        source = data.weeks.flat();
      } else {
        source = data.weeks[monthWeek - 1] || [];
      }

      return {
        labels: source.map(d => d.date),
        data: source.map(d => ({
          success: d.success,
          pending: d.pending,
          refunded: d.refunded,
          paused: d.paused
        }))
      };
    }

  }, [analytics, view, monthWeek]);

  if (!graphData) return null;

  const success = graphData.data.map(d => d.success);
  const pending = graphData.data.map(d => d.pending);
  const refund  = graphData.data.map(d => d.refunded);
  const paused  = graphData.data.map(d => d.paused);
  const total   = graphData.data.map(d => d.success + d.pending + d.refunded + d.paused);

  const sum = arr => arr.reduce((a,b)=>a+b,0);

  /* ================= CARD ================= */

  const CardBox = ({ title, value, seriesData, color }) => (

    <Card style={{
      borderRadius: 18,
      boxShadow: "0 4px 25px rgba(0,0,0,0.06)"
    }}>
      <Card.Body>

        <div className="d-flex justify-content-between">
          <div>
            <h3 style={{ fontWeight: 600 }}>{value}</h3>
            <div style={{ color: "#6c757d" }}>{title}</div>
          </div>
          <div style={{ color: "green", fontWeight: 500 }}>▲ 0%</div>
        </div>

        <ReactApexChart
          type="area"
          height={90}
          series={[{ name: title, data: seriesData }]}
          options={{
            chart: {
              sparkline: { enabled: true }
            },
            stroke: {
              curve: "smooth",
              width: 3
            },
            colors: [color],
            fill: {
              type: "gradient",
              gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05
              }
            },
            tooltip: {
              enabled: true,
              custom: function({ dataPointIndex }) {

                const point = graphData.data[dataPointIndex];
                const label = graphData.labels[dataPointIndex];

                return `
                  <div style="
                    padding:12px;
                    background:#fff;
                    border-radius:12px;
                    box-shadow:0 6px 25px rgba(0,0,0,0.1);
                    font-family:Inter, sans-serif;
                    min-width:170px;
                  ">
                    <div style="font-weight:600;margin-bottom:8px;">
                      ${label}
                    </div>
                    <div style="font-size:14px;line-height:1.9">
                      <div>Success: ${point.success}</div>
                      <div>Pending: ${point.pending}</div>
                      <div>Refund: ${point.refunded}</div>
                      <div>Paused: ${point.paused}</div>
                    </div>
                  </div>
                `;
              }
            }
          }}
        />

      </Card.Body>
    </Card>
  );

  /* ================= UI ================= */

  return (
    <div style={{ padding: 20, background: "#f5f7fb", borderRadius: 20 }}>

      <Row className="g-2 mb-4 align-items-center">

        <Col md="auto">
          <Button variant={view==="total"?"primary":"outline-primary"} onClick={()=>setView("total")}>Total</Button>
        </Col>

        <Col md="auto">
          <Button variant={view==="year"?"primary":"outline-primary"} onClick={()=>setView("year")}>Year</Button>
        </Col>

        <Col md="auto">
          <Button variant={view==="month"?"primary":"outline-primary"} onClick={()=>setView("month")}>Month</Button>
        </Col>

        <Col md="auto">
          <Form.Select value={year} onChange={e=>setYear(+e.target.value)}>
            {[2024,2025,2026,2027].map(y=><option key={y}>{y}</option>)}
          </Form.Select>
        </Col>

        {view==="month" && (
          <>
            <Col md="auto">
              <Form.Select value={month} onChange={e=>setMonth(+e.target.value)}>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                  .map((m,i)=><option key={i} value={i}>{m}</option>)}
              </Form.Select>
            </Col>

            <Col md="auto">
              <Form.Select value={monthWeek} onChange={e=>setMonthWeek(e.target.value)}>
                <option value="all">All</option>
                <option value={1}>Week 1</option>
                <option value={2}>Week 2</option>
                <option value={3}>Week 3</option>
                <option value={4}>Week 4</option>
                <option value={5}>Week 5</option>
              </Form.Select>
            </Col>
          </>
        )}

      </Row>

      <Row className="g-4">

        <Col md={6}>
          <CardBox title="Success" value={sum(success)} seriesData={success} color="#3B82F6"/>
        </Col>

        <Col md={6}>
          <CardBox title="Pending" value={sum(pending)} seriesData={pending} color="#10B981"/>
        </Col>

        <Col md={6}>
          <CardBox title="Refund" value={sum(refund)} seriesData={refund} color="#F59E0B"/>
        </Col>

        <Col md={6}>
          <CardBox title="Total" value={sum(total)} seriesData={total} color="#6366F1"/>
        </Col>

      </Row>

    </div>
  );
}